import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { activeAlarmsQuery, alarmSummaryQuery, closeAlarm, type ActiveAlarm, type Door } from '../api/alarms'
import { ApiError } from '../api/client'
import { AreaChart } from './AreaChart'
import { convertValues } from './columns'
import { Spinner } from '../ui/Spinner'

/**
 * Randul desfasurat al unei definitii de alarma - alarms/details/tabList.php
 * si cele doua file ale lui, alarms/summary.php si alarms/activeAlarms.php,
 * cu acelasi markup si aceleasi gesturi din general.js (ExpandRow) si
 * proactivitee.js (OpteemumAlarms.closeAlarms).
 */

export type Tab = 'summary' | 'active'

export interface AlarmDetailsProps {
  door: Door
  id: number
  tabs: { summary: boolean; active: boolean }
  unit: 'C' | 'F'
}

export { Spinner }

/** odPanelFailure(xhr) din general.js. */
export function failureText(error: unknown): string {
  if (error instanceof ApiError) return `HTTP ${error.status} - ${error.message}`
  return 'no answer from the server'
}

function Failure({ error }: { error: unknown }) {
  return (
    <div className="col-md-12">
      <div className="alert alert-danger">
        <strong>This did not load.</strong> <span style={{ fontSize: 12 }}>{failureText(error)}</span>
      </div>
    </div>
  )
}

/** $.animateNumber: cifra urca de la 0 in 400 ms (easing swing). */
export function AnimatedNumber({ value }: { value: number }) {
  const animated = typeof requestAnimationFrame === 'function'
  const [shown, setShown] = useState(animated ? 0 : value)
  useEffect(() => {
    if (!animated) return
    const start = performance.now()
    let frame = 0
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 400)
      const swing = 0.5 - Math.cos(t * Math.PI) / 2
      setShown(Math.round(value * swing))
      if (t < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [value, animated])
  return (
    <div className="number number-summary" data-number={value}>
      {shown}
    </div>
  )
}

const STATS: { key: 'active' | 'closed' | 'total' | 'closed_by_users'; label: string }[] = [
  { key: 'active', label: 'Active alarms' },
  { key: 'closed', label: 'Closed alarms' },
  { key: 'total', label: 'Total alarms' },
  { key: 'closed_by_users', label: 'Closed by users' },
]

function SummaryTab({ door, id }: { door: Door; id: number }) {
  const summary = useQuery(alarmSummaryQuery(door, id))
  if (summary.isPending) return <Spinner />
  if (summary.error) return <Failure error={summary.error} />
  const data = summary.data
  return (
    <>
      <div style={{ float: 'left', width: '100%' }}>
        {STATS.map((stat) => (
          /* `col-md-3` si nu `col-3`, cum scrie macheta veche
             (alarms/summary.php): app.css e Bootstrap 3, unde clasa fara
             prag NU EXISTA - nici in ea, nici in vreo alta foaie a
             portalului. Fara regula, cele patru cartonase cad la latimea
             lor fireasca, adica toata, si se aseaza unul sub altul. Cu
             `col-md-3` stau in rand cate patru peste 992px si se aseaza
             unul sub altul dedesubt, ca peste tot in portal. */
          <div className="col-md-3" key={stat.key}>
            <div className="dashboard-stat red-intense">
              <div className="visual">
                <i className="fa fa-bell"></i>
              </div>
              <div className="details">
                <AnimatedNumber value={data.counters[stat.key]} />
                <div className="desc">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ float: 'left', width: '100%', padding: 15, boxSizing: 'border-box' }}>
        <div className="widget border-red color-red">
          <div className="widget-header bg-red">
            <div className="widget-icon">
              <i className="fas fa-chart-area"></i>
            </div>
            <h2 className="widget-title">Alarms last 7 days</h2>
            <div className="widget-action">
              <button className="btn hide-widget" type="button">
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
          <div className="widget-content chart_7_days" style={{ height: 250 }} data-alert={id}>
            <AreaChart categories={data.series.categories} data={data.series.data} yTitle="Alarms" height={250} />
          </div>
        </div>
      </div>
    </>
  )
}

/** Iconita masinii din alarms/activeAlarm.php, dupa Tip. */
export function deviceIcon(type: number | string | null): string {
  switch (String(type)) {
    case '2':
      return 'fas fa-laptop'
    case '3':
      return 'fas fa-server'
    case '11':
      return 'fas fa-mobile-alt'
    case '12':
      return 'fas fa-tablet-alt'
    default:
      return 'fas fa-desktop'
  }
}

/** Valoarea de langa identificator, cum o scrie activeAlarm.php (HTML, cu <sup>). */
export function tileValueHtml(alarm: ActiveAlarm, unit: 'C' | 'F'): string {
  if (String(alarm.dinamic) !== '1') return ''
  if (alarm.convert === 'TEMP') return convertValues(alarm.value_numeric, unit, alarm.sign)
  return String(alarm.value_numeric ?? '') + String(alarm.sign ?? '')
}

function Tile({ alarm, unit, closing, onClose }: { alarm: ActiveAlarm; unit: 'C' | 'F'; closing: boolean; onClose: () => void }) {
  const [hover, setHover] = useState(false)
  const person = alarm.Nume ? `${alarm.Prenume ?? ''} ${alarm.Nume}` : ''
  return (
    <div
      className="tile bg-red-sunglo"
      style={closing ? { opacity: 0, transition: 'opacity 200ms' } : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="tile-body">
        <i className={deviceIcon(alarm.device_type)}></i>
        <p>{alarm.Denumire}</p>
        {alarm.Nume ? (
          <em>
            <i className="fa fa-user"></i> {person}
          </em>
        ) : null}
      </div>
      <div className="tile-object">
        <div className="name">
          {alarm.hardware_identifier} <span dangerouslySetInnerHTML={{ __html: tileValueHtml(alarm, unit) }} />
        </div>
        <div className="actions" style={{ display: hover ? 'block' : 'none' }}>
          <form
            className="form-close-alarm"
            method="post"
            onSubmit={(e) => {
              e.preventDefault()
              if (!window.confirm('Are you sure?')) return
              onClose()
            }}
          >
            <input type="hidden" value={String(alarm.ID)} name="alarm" />
            <button type="submit" className="bg-white color-red-sunglo">
              Close
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

function NoActiveAlarms() {
  return (
    <div className="col-md-12">
      <div className="alert alert-info">
        <i className="fas fa-exclamation-circle"></i> No active alarms.
      </div>
    </div>
  )
}

function ActiveTab({ door, id, unit }: { door: Door; id: number; unit: 'C' | 'F' }) {
  const active = useQuery(activeAlarmsQuery(door, id))
  const [gone, setGone] = useState<string[]>([])
  const [closing, setClosing] = useState<string[]>([])

  if (active.isPending) return <Spinner />
  if (active.error) return <Failure error={active.error} />

  const alarms = active.data.alarms.filter((a) => !gone.includes(String(a.ID)))
  if (alarms.length === 0) return <NoActiveAlarms />

  const close = async (alarm: ActiveAlarm) => {
    const key = String(alarm.ID)
    try {
      await closeAlarm(door, Number(alarm.ID))
    } catch {
      /* ca in vechi: la eroare, cartonasul ramane la locul lui */
      return
    }
    /* Ca in vechi, lista de deasupra nu se redeseneaza: cifra ei se schimba la
       urmatoarea incarcare, iar randul desfasurat ramane deschis. */
    setClosing((list) => [...list, key])
    window.setTimeout(() => {
      setGone((list) => [...list, key])
      setClosing((list) => list.filter((k) => k !== key))
    }, 200)
  }

  return (
    <>
      {alarms.map((alarm) => (
        <Tile key={String(alarm.ID)} alarm={alarm} unit={unit} closing={closing.includes(String(alarm.ID))} onClose={() => void close(alarm)} />
      ))}
    </>
  )
}

export function AlarmDetails({ door, id, tabs, unit }: AlarmDetailsProps) {
  const available: Tab[] = [...(tabs.summary ? (['summary'] as Tab[]) : []), ...(tabs.active ? (['active'] as Tab[]) : [])]
  const [tab, setTab] = useState<Tab | null>(available[0] ?? null)
  /* Clicul pe fila deja deschisa o reincarca (ExpandRow scoate `active` si o pune la loc). */
  const [reload, setReload] = useState(0)
  const client = useQueryClient()

  const pick = (next: Tab) => {
    if (next === tab) {
      client.removeQueries({ queryKey: ['alarms', 'definition', door, id, next] })
      setReload((n) => n + 1)
    } else {
      setTab(next)
    }
  }

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.summary && (
            <li>
              <a
                href={`/${door}/alarmSummary/${id}`}
                className={tab === 'summary' ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  pick('summary')
                }}
              >
                Summary
              </a>
            </li>
          )}
          {tabs.active && (
            <li>
              <a
                href={`/${door}/activeAlarms/${id}`}
                className={tab === 'active' ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  pick('active')
                }}
              >
                Active alarms
              </a>
            </li>
          )}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'summary' && <SummaryTab key={`s${reload}`} door={door} id={id} />}
          {tab === 'active' && <ActiveTab key={`a${reload}`} door={door} id={id} unit={unit} />}
        </div>
      </div>
    </div>
  )
}
